// src/pages/ventes/DetailsReglement.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Button, Spinner, Alert,
    Modal, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

export default function DetailsReglement() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [reglement, setReglement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });
    const showSuccess = (msg) => showToast('success', 'Succès', msg);
    const showError   = (msg) => showToast('error', 'Erreur', msg);

    useEffect(() => {
        fetchReglement();
    }, [id]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchReglement = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/reglements.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setReglement(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleValider = async () => {
        setIsProcessing(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/reglements.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'valider', id: id })
            });
            const result = await response.json();
            if (result.success) {
                setShowConfirmModal(false);
                fetchReglement();
                showSuccess("Règlement validé avec succès");
            } else {
                showError(result.message || "Erreur lors de la validation");
            }
        } catch (err) {
            console.error("Erreur:", err);
            showError("Erreur de connexion au serveur");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSupprimer = async () => {
        setIsProcessing(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/reglements.php?id=${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                showSuccess("Règlement supprimé avec succès");
                setTimeout(() => navigate('/ventes/reglements'), 1500);
            } else {
                showError(result.message || "Erreur lors de la suppression");
            }
        } catch (err) {
            console.error("Erreur:", err);
            showError("Erreur de connexion au serveur");
        } finally {
            setIsProcessing(false);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getStatutConfig = (statut) => {
        const configs = {
            en_attente: { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', icon: 'ri-time-line',         label: 'En attente' },
            valide:     { gradient: 'linear-gradient(135deg, #10b981, #059669)', icon: 'ri-check-double-line', label: 'Validé' },
            rejete:     { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', icon: 'ri-close-circle-line', label: 'Rejeté' }
        };
        return configs[statut] || configs.en_attente;
    };

    const getStatutBadge = (statut) => {
        const config = getStatutConfig(statut);
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    const getModeIcon = (mode) => {
        const icons = {
            especes: 'ri-money-euro-circle-line',
            cheque: 'ri-bank-card-line',
            virement: 'ri-bank-line',
            carte_bancaire: 'ri-smartphone-line',
            traite: 'ri-file-copy-line'
        };
        return icons[mode] || 'ri-bank-card-line';
    };

    const getModeLabel = (mode) => {
        const labels = {
            especes: 'Espèces',
            cheque: 'Chèque',
            virement: 'Virement bancaire',
            carte_bancaire: 'Carte bancaire',
            traite: 'Traite'
        };
        return labels[mode] || mode;
    };

    /* ============================================================
       LOADING / ERROR
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-bank-card-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement du règlement
                        </h5>
                        <p className="text-muted small">Récupération des informations en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !reglement) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Règlement non trouvé</h6>
                                <p className="mb-0 small">{error || "Le règlement demandé n'existe pas"}</p>
                            </div>
                            <Button onClick={() => navigate('/ventes/reglements')} className="btn-modern-outline">
                                <i className="ri-arrow-left-line me-1"></i>Retour
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-bank-card-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Règlement {reglement.numero_reglement}</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-calendar-line"></i>
                                        {formatDate(reglement.date_reglement)}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                        <i className="ri-money-euro-circle-line"></i>
                                        {formatCurrency(reglement.montant)}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {getStatutBadge(reglement.statut)}
                            <div className="d-flex gap-2">
                                <Button className="btn-modern-outline" onClick={() => navigate('/ventes/reglements')}>
                                    <i className="ri-arrow-left-line"></i> Retour
                                </Button>
                                {reglement.statut === 'en_attente' && (
                                    <Button
                                        className="btn-modern-success"
                                        onClick={() => {
                                            setConfirmAction('valider');
                                            setShowConfirmModal(true);
                                        }}
                                    >
                                        <i className="ri-check-double-line me-1"></i> Valider
                                    </Button>
                                )}
                                <Button
                                    className="btn-modern-danger"
                                    onClick={() => {
                                        setConfirmAction('supprimer');
                                        setShowConfirmModal(true);
                                    }}
                                >
                                    <i className="ri-delete-bin-line me-1"></i> Supprimer
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* ===== CARTE STATS ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center g-3">
                                <Col md={3}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-barcode-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">N° Règlement</span>
                                            <div className="stat-value-charte" style={{ color: COLORS.primary }}>{reglement.numero_reglement}</div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                            <i className="ri-calendar-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">Date</span>
                                            <div className="stat-value-charte">{formatDate(reglement.date_reglement)}</div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                            <i className="ri-money-euro-circle-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">Montant</span>
                                            <div className="stat-value-charte" style={{ color: COLORS.primary, fontWeight: 700 }}>
                                                {formatCurrency(reglement.montant)}
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="reste-box-charte">
                                        <span className="small text-muted d-block">Statut</span>
                                        <div className="mt-2">{getStatutBadge(reglement.statut)}</div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== INFORMATIONS GÉNÉRALES + CLIENT ===== */}
                    <Row className="g-3 mb-4">
                        <Col md={8}>
                            <Card className="border-0 shadow-sm rounded-3 h-100 card-hover-charte">
                                <Card.Header className="card-section-header">
                                    <div className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                        <i className="ri-information-line"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Informations générales</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={6}>
                                            <div className="info-row-charte">
                                                <span className="info-label-charte">
                                                    <i className="ri-file-text-line"></i>N° Règlement
                                                </span>
                                                <span className="info-value-charte">{reglement.numero_reglement}</span>
                                            </div>
                                            <div className="info-row-charte">
                                                <span className="info-label-charte">
                                                    <i className="ri-calendar-line"></i>Date
                                                </span>
                                                <span className="info-value-charte">{formatDate(reglement.date_reglement)}</span>
                                            </div>
                                            <div className="info-row-charte">
                                                <span className="info-label-charte">
                                                    <i className={getModeIcon(reglement.mode_reglement)}></i>Mode
                                                </span>
                                                <span className="info-value-charte">
                                                    <span className="mode-badge-charte">
                                                        <i className={getModeIcon(reglement.mode_reglement)}></i>
                                                        {getModeLabel(reglement.mode_reglement)}
                                                    </span>
                                                </span>
                                            </div>
                                            {reglement.reference && (
                                                <div className="info-row-charte">
                                                    <span className="info-label-charte">
                                                        <i className="ri-qr-code-line"></i>Référence
                                                    </span>
                                                    <span className="info-value-charte">{reglement.reference}</span>
                                                </div>
                                            )}
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-row-charte">
                                                <span className="info-label-charte">
                                                    <i className="ri-money-euro-circle-line"></i>Montant
                                                </span>
                                                <span className="info-value-charte fw-bold" style={{ color: COLORS.primary, fontSize: '1.15rem' }}>
                                                    {formatCurrency(reglement.montant)}
                                                </span>
                                            </div>
                                            <div className="info-row-charte">
                                                <span className="info-label-charte">
                                                    <i className="ri-user-line"></i>Saisi par
                                                </span>
                                                <span className="info-value-charte">
                                                    {reglement.prenom_utilisateur} {reglement.nom_utilisateur}
                                                </span>
                                            </div>
                                            <div className="info-row-charte">
                                                <span className="info-label-charte">
                                                    <i className="ri-time-line"></i>Date création
                                                </span>
                                                <span className="info-value-charte">{formatDate(reglement.date_creation)}</span>
                                            </div>
                                        </Col>
                                    </Row>

                                    {reglement.commentaire && (
                                        <div className="comment-box-charte mt-3">
                                            <div className="comment-header-charte">
                                                <i className="ri-chat-1-line"></i>
                                                Commentaire
                                            </div>
                                            <div className="comment-content-charte">
                                                {reglement.commentaire}
                                            </div>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={4}>
                            <Card className="border-0 shadow-sm rounded-3 h-100 card-hover-charte">
                                <Card.Header className="card-section-header">
                                    <div className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-user-line"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Client</h6>
                                </Card.Header>
                                <Card.Body className="text-center">
                                    <div className="client-avatar-charte">
                                        <i className="ri-user-line"></i>
                                    </div>
                                    <h5 className="fw-bold mb-3">{reglement.client_nom}</h5>
                                    <div className="client-details-charte">
                                        <p className="mb-2">
                                            <i className="ri-barcode-line"></i>
                                            <span>Code: <strong>{reglement.code_client}</strong></span>
                                        </p>
                                        {reglement.client_telephone && (
                                            <p className="mb-2">
                                                <i className="ri-phone-line"></i>
                                                <span>{reglement.client_telephone}</span>
                                            </p>
                                        )}
                                        {reglement.client_email && (
                                            <p className="mb-2">
                                                <i className="ri-mail-line"></i>
                                                <span>{reglement.client_email}</span>
                                            </p>
                                        )}
                                    </div>
                                    <Button
                                        className="btn-modern-outline w-100 justify-content-center mt-3"
                                        onClick={() => navigate(`/ventes/clientsDetails/${reglement.id_client}`)}
                                    >
                                        <i className="ri-eye-line me-2"></i>Voir la fiche client
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== FACTURE ASSOCIÉE ===== */}
                    {reglement.id_facture && (
                        <Card className="border-0 shadow-sm rounded-3">
                            <Card.Header className="card-section-header">
                                <div className="section-icon-charte" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                    <i className="ri-file-list-3-line"></i>
                                </div>
                                <h6 className="mb-0 fw-semibold">Facture associée</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <div className="info-row-charte">
                                            <span className="info-label-charte">
                                                <i className="ri-barcode-line"></i>N° Facture
                                            </span>
                                            <span className="info-value-charte">
                                                <Link to={`/ventes/facturesDetails/${reglement.id_facture}`} className="table-link-charte">
                                                    {reglement.numero_facture}
                                                </Link>
                                            </span>
                                        </div>
                                        <div className="info-row-charte">
                                            <span className="info-label-charte">
                                                <i className="ri-calculator-line"></i>Montant TTC
                                            </span>
                                            <span className="info-value-charte fw-bold">
                                                {formatCurrency(reglement.facture_montant)}
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="info-row-charte">
                                            <span className="info-label-charte">
                                                <i className="ri-funds-line"></i>Solde avant
                                            </span>
                                            <span className="info-value-charte">
                                                {formatCurrency((reglement.facture_solde || 0) + (reglement.montant || 0))}
                                            </span>
                                        </div>
                                        <div className="info-row-charte">
                                            <span className="info-label-charte">
                                                <i className="ri-check-double-line" style={{ color: COLORS.success }}></i>Solde après
                                            </span>
                                            <span className="info-value-charte fw-bold" style={{ color: COLORS.primary, fontSize: '1.15rem' }}>
                                                {formatCurrency(reglement.facture_solde || 0)}
                                            </span>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== MODAL CONFIRMATION ===== */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered className="modal-charte">
                <Modal.Header
                    closeButton
                    className="modal-header-charte"
                    style={{
                        background: confirmAction === 'valider'
                            ? `linear-gradient(135deg, ${COLORS.success}, #059669)`
                            : `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`
                    }}
                >
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon">
                            <i className={confirmAction === 'valider' ? 'ri-check-double-line' : 'ri-delete-bin-line'}></i>
                        </div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">
                                {confirmAction === 'valider' ? 'Valider le règlement' : 'Supprimer le règlement'}
                            </Modal.Title>
                            <p className="text-white-50 mb-0 small">Confirmation requise</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4 text-center">
                    {confirmAction === 'valider' ? (
                        <>
                            <p className="text-muted mb-4">
                                Êtes-vous sûr de vouloir valider ce règlement ?
                            </p>
                            <Alert className="alert-info-charte text-start mb-0">
                                <div className="d-flex align-items-start gap-3">
                                    <i className="ri-information-line fs-4"></i>
                                    <div>
                                        <small className="d-block">✓ Le solde du client sera mis à jour</small>
                                        <small className="d-block">✓ Le statut de la facture sera mis à jour</small>
                                    </div>
                                </div>
                            </Alert>
                        </>
                    ) : (
                        <>
                            <p className="text-muted mb-4">
                                Êtes-vous sûr de vouloir supprimer ce règlement ?
                            </p>
                            <Alert className="alert-error-charte mb-0">
                                <div className="d-flex align-items-center">
                                    <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                                    <div className="ms-3">
                                        <strong>Attention</strong>
                                        <p className="mb-0 small">Cette action est irréversible</p>
                                    </div>
                                </div>
                            </Alert>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowConfirmModal(false)} className="btn-modern-outline" disabled={isProcessing}>
                        Annuler
                    </Button>
                    <Button
                        onClick={confirmAction === 'valider' ? handleValider : handleSupprimer}
                        disabled={isProcessing}
                        className={confirmAction === 'valider' ? 'btn-modern-success' : 'btn-modern-danger'}
                    >
                        {isProcessing ? (
                            <><Spinner size="sm" className="me-2" />Traitement...</>
                        ) : (
                            <><i className="ri-check-line me-2"></i>Confirmer</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .status-badge-charte {
                    color: white; padding: 0.4rem 1.2rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 0.5rem;
                }

                /* ===== CARTE STATS ===== */
                .stat-item-charte { display: flex; align-items: center; gap: 0.75rem; }
                .stat-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .stat-label-charte {
                    display: block; font-size: 0.7rem; font-weight: 500;
                    text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8;
                }
                .stat-value-charte { font-size: 1.05rem; font-weight: 600; color: #1e293b; }
                .reste-box-charte {
                    padding: 0.75rem 1.25rem; border-radius: 12px;
                    text-align: center;
                    background: #f8fafc;
                    border: 1px solid #f1f5f9;
                    display: inline-block;
                    width: 100%;
                }

                /* ===== SECTION HEADER ===== */
                .card-section-header {
                    background: transparent; border: none;
                    padding: 1.25rem 1.5rem 0.75rem;
                    display: flex; align-items: center; gap: 0.75rem;
                }
                .section-icon-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.1rem;
                }
                .card-hover-charte { transition: all 0.3s ease; }
                .card-hover-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 32px rgba(0,0,0,0.08) !important;
                }

                /* ===== INFO ROW ===== */
                .info-row-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 0.75rem 0;
                    border-bottom: 1px dashed #e2e8f0;
                    gap: 1rem;
                }
                .info-row-charte:last-child { border-bottom: none; }
                .info-label-charte {
                    display: inline-flex; align-items: center; gap: 8px;
                    color: #64748b; font-size: 0.8rem; font-weight: 500;
                }
                .info-label-charte i { color: var(--secondary); font-size: 0.9rem; }
                .info-value-charte {
                    font-weight: 600; color: #1e293b;
                    font-size: 0.85rem; text-align: right;
                }
                .mode-badge-charte {
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 0.3rem 0.85rem;
                    background: ${COLORS.secondary}15;
                    border-radius: 50px;
                    font-size: 0.75rem;
                    color: var(--secondary);
                    font-weight: 600;
                }

                /* ===== COMMENT BOX ===== */
                .comment-box-charte {
                    padding: 1rem 1.25rem;
                    background: #f8fafc;
                    border-radius: 12px;
                    border-left: 4px solid var(--secondary);
                }
                .comment-header-charte {
                    display: flex; align-items: center; gap: 8px;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                    color: var(--secondary);
                    font-size: 0.85rem;
                }
                .comment-content-charte {
                    font-size: 0.85rem;
                    color: #475569;
                    line-height: 1.6;
                }

                /* ===== CLIENT CARD ===== */
                .client-avatar-charte {
                    width: 80px; height: 80px;
                    border-radius: 20px;
                    background: linear-gradient(135deg, ${COLORS.primary}15, ${COLORS.secondary}15);
                    color: var(--secondary);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 2rem;
                    margin: 0 auto 1rem;
                    border: 2px solid ${COLORS.secondary}25;
                }
                .client-details-charte {
                    padding: 0.5rem 0;
                    text-align: left;
                }
                .client-details-charte p {
                    display: flex; align-items: center; gap: 8px;
                    color: #475569;
                    font-size: 0.85rem;
                }
                .client-details-charte p i {
                    color: var(--secondary);
                    font-size: 1rem;
                    min-width: 18px;
                }

                /* ===== TABLE LINK ===== */
                .table-link-charte {
                    color: var(--primary); text-decoration: none;
                    font-weight: 600;
                    transition: color 0.2s;
                }
                .table-link-charte:hover {
                    color: var(--primary-dark);
                    text-decoration: underline;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-success:disabled,
                .btn-modern-danger:disabled { opacity: 0.6; transform: none; }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }

                /* ===== ALERTES ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }
                .alert-info-charte {
                    border: none; background: #eff6ff;
                    border-left: 4px solid var(--info);
                    border-radius: 12px; padding: 1rem 1.25rem;
                    font-size: 0.85rem; color: #1e40af;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .reste-box-charte { text-align: left; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                    .info-value-charte { font-size: 0.8rem; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}